// Cmd+K command palette — discoverable surface for every action
// the harness can perform, plus the one place to find out which
// keybindings exist.
//
// Design notes:
//   - All commands come in through a single prop from App. App is
//     the source of truth for handlers; the palette is pure UI.
//   - Search is simple substring-over-tokens matching. Good enough
//     for a fixed vocabulary of ~30-50 commands; a trie or fuzzy
//     lib would be over-kill.
//   - Commands are grouped by section. Matching filters each section
//     independently so users see the grouping preserved while they
//     type.
//   - Keyboard: ↑↓ to move cursor within the filtered list, Enter to
//     run, Esc to close. Mouse hover updates the cursor too, so mouse
//     + keyboard interleave cleanly.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type Command = {
  /** Stable id — used as the React list key. */
  id: string;
  /** Primary human-readable label. */
  label: string;
  /** Extra secondary line under the label. */
  description?: string;
  /** Bucket the command appears under in the unfiltered list. */
  section: string;
  /** Display-only shortcut hint, e.g. "⌘S". No behaviour attached — the
   *  actual keybinding lives wherever the App wires up keydown events. */
  shortcut?: string;
  /** Extra words the fuzzy-ish search should consider beyond `label`. */
  keywords?: string[];
  /** Whether this command is currently runnable. Disabled commands are
   *  still listed (useful for discoverability) but can't be Enter'd. */
  disabled?: boolean;
  run: () => void;
};

type Props = {
  open: boolean;
  onClose: () => void;
  commands: Command[];
};

export function CommandPalette({ open, onClose, commands }: Props) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset query + cursor on each open so stale state never carries
  // over between invocations.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
    // Focus input next frame so the modal's slide-in finishes first.
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Flat list of (runnable) commands in visible order. The same list
  // drives both keyboard navigation and the rendered sections, so
  // index math stays coherent.
  const { sections, flat } = useMemo(
    () => filterAndGroup(commands, query),
    [commands, query],
  );

  // Keep cursor in bounds as the filter shrinks the list.
  useEffect(() => {
    if (cursor >= flat.length) setCursor(Math.max(0, flat.length - 1));
  }, [cursor, flat.length]);

  const runIndex = useCallback(
    (idx: number) => {
      const cmd = flat[idx];
      if (!cmd || cmd.disabled) return;
      cmd.run();
      onClose();
    },
    [flat, onClose],
  );

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(flat.length - 1, c + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        setCursor(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        setCursor(flat.length - 1);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        runIndex(cursor);
      }
    },
    [flat.length, cursor, onClose, runIndex],
  );

  // Scroll the active row into view when the cursor moves.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-cmd-index="${cursor}"]`,
    );
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [cursor, open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="command-palette-title"
      className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-zinc-950/85 backdrop-blur-md starter-kit-fade-in"
        onClick={onClose}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-screen"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)',
        }}
      />

      <div className="relative w-full max-w-[640px] max-h-[72vh] flex flex-col rounded-md border border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.8)] starter-kit-panel-in overflow-hidden">
        <PaletteHeader query={query} setQuery={setQuery} onKey={handleKey} inputRef={inputRef} />

        <div
          ref={listRef}
          role="listbox"
          aria-labelledby="command-palette-title"
          className="flex-1 overflow-y-auto scrollbar-thin px-2 py-2"
        >
          {sections.length === 0 ? (
            <EmptyState query={query} />
          ) : (
            sections.map((section) => (
              <SectionGroup key={section.label} label={section.label}>
                {section.items.map(({ command, globalIndex }) => (
                  <CommandRow
                    key={command.id}
                    command={command}
                    active={globalIndex === cursor}
                    onMouseEnter={() => setCursor(globalIndex)}
                    onClick={() => runIndex(globalIndex)}
                    dataIndex={globalIndex}
                  />
                ))}
              </SectionGroup>
            ))
          )}
        </div>

        <PaletteFooter count={flat.length} totalCount={commands.length} />
      </div>
    </div>
  );
}

// ─── Header / footer ────────────────────────────────────────────────

function PaletteHeader({
  query,
  setQuery,
  onKey,
  inputRef,
}: {
  query: string;
  setQuery: (q: string) => void;
  onKey: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <header className="relative px-4 pt-4 pb-3 border-b border-zinc-900 bg-gradient-to-b from-zinc-900/50 to-transparent">
      <div className="flex items-center gap-2.5 mb-2.5">
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full bg-yellow-300 shadow-[0_0_10px_rgba(253,224,71,0.7)]"
        />
        <h2
          id="command-palette-title"
          className="text-yellow-300 text-[10px] tracking-[0.38em] uppercase font-display leading-none"
        >
          Command palette
        </h2>
        <span className="ml-auto text-[9px] tracking-[0.22em] uppercase font-display text-zinc-600">
          ⌘K
        </span>
      </div>

      <label className="relative block">
        <span className="sr-only">Search commands</span>
        <svg
          viewBox="0 0 16 16"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600"
          stroke="currentColor"
          strokeWidth={1.5}
          fill="none"
          aria-hidden="true"
        >
          <circle cx={7} cy={7} r={4.5} />
          <path d="M10.5 10.5 L14 14" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          placeholder="Type a command…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-yellow-300/50 focus:bg-zinc-900 focus:outline-none rounded text-[13px] text-zinc-100 placeholder:text-zinc-600 pl-8 pr-3 py-2 transition-colors font-body"
        />
      </label>
    </header>
  );
}

function PaletteFooter({
  count,
  totalCount,
}: {
  count: number;
  totalCount: number;
}) {
  return (
    <footer className="px-4 py-2 border-t border-zinc-900 bg-zinc-900/30 flex items-center justify-between text-[9px] tracking-[0.25em] uppercase font-display text-zinc-600">
      <span>
        {count === totalCount ? `${totalCount} commands` : `${count} / ${totalCount} match`}
      </span>
      <span className="flex items-center gap-3">
        <kbd className="font-body normal-case tracking-normal text-[10px] text-zinc-500">↑↓</kbd>
        <span>move</span>
        <span className="text-zinc-800">·</span>
        <kbd className="font-body normal-case tracking-normal text-[10px] text-zinc-500">↵</kbd>
        <span>run</span>
        <span className="text-zinc-800">·</span>
        <kbd className="font-body normal-case tracking-normal text-[10px] text-zinc-500">esc</kbd>
        <span>close</span>
      </span>
    </footer>
  );
}

// ─── Section / row ──────────────────────────────────────────────────

function SectionGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="pb-2">
      <div className="px-2 py-1 text-[8.5px] tracking-[0.3em] uppercase font-display text-zinc-600">
        {label}
      </div>
      <div role="group" aria-label={label} className="space-y-0.5">
        {children}
      </div>
    </div>
  );
}

function CommandRow({
  command,
  active,
  onMouseEnter,
  onClick,
  dataIndex,
}: {
  command: Command;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  dataIndex: number;
}) {
  return (
    <div
      role="option"
      aria-selected={active}
      aria-disabled={command.disabled}
      data-cmd-index={dataIndex}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={
        'flex items-center gap-3 px-3 py-1.5 rounded-sm cursor-pointer transition-colors ' +
        (active
          ? 'bg-yellow-300/[0.08] border border-yellow-300/40'
          : 'border border-transparent hover:bg-zinc-900/60') +
        (command.disabled ? ' opacity-40 cursor-not-allowed' : '')
      }
    >
      <div className="flex-1 min-w-0">
        <div
          className={
            'font-body text-[12.5px] leading-tight truncate ' +
            (active ? 'text-yellow-300' : 'text-zinc-100')
          }
        >
          {command.label}
        </div>
        {command.description && (
          <div className="text-[10.5px] text-zinc-500 mt-0.5 truncate">
            {command.description}
          </div>
        )}
      </div>
      {command.shortcut && (
        <span className="shrink-0 font-body text-[10px] text-zinc-500 tracking-wide border border-zinc-800 rounded-sm px-1.5 py-0.5 bg-zinc-950/60">
          {command.shortcut}
        </span>
      )}
    </div>
  );
}

function EmptyState({ query }: { query: string }) {
  return (
    <div className="py-10 text-center">
      <div className="font-display text-[10px] tracking-[0.32em] uppercase text-zinc-600 mb-2">
        nothing matches
      </div>
      <p className="text-[11px] text-zinc-500 leading-[1.5]">
        No command found for{' '}
        <span className="font-mono text-zinc-300">
          &ldquo;{query}&rdquo;
        </span>
        .
      </p>
    </div>
  );
}

// ─── Search + grouping ──────────────────────────────────────────────
//
// Keep the list in the order App provides it. Within each section,
// preserve the provided order too. This matches how users scan a
// static "menu" — the unfiltered view has a predictable layout.

type Grouped = {
  sections: {
    label: string;
    items: { command: Command; globalIndex: number }[];
  }[];
  flat: Command[];
};

function filterAndGroup(commands: Command[], rawQuery: string): Grouped {
  const query = rawQuery.trim().toLowerCase();
  const matches = query
    ? commands.filter((c) => matchesQuery(c, query))
    : commands;

  const sections: Grouped['sections'] = [];
  const flat: Command[] = [];
  const sectionIndex = new Map<string, number>();

  for (const cmd of matches) {
    const sectionLabel = cmd.section || 'Other';
    let idx = sectionIndex.get(sectionLabel);
    if (idx === undefined) {
      idx = sections.length;
      sectionIndex.set(sectionLabel, idx);
      sections.push({ label: sectionLabel, items: [] });
    }
    const globalIndex = flat.length;
    sections[idx]!.items.push({ command: cmd, globalIndex });
    flat.push(cmd);
  }

  return { sections, flat };
}

function matchesQuery(cmd: Command, query: string): boolean {
  // Split the query on whitespace so "mute kick" works even when
  // neither word individually matches a label.
  const needles = query.split(/\s+/).filter(Boolean);
  const hay = [
    cmd.label,
    cmd.section,
    cmd.description ?? '',
    ...(cmd.keywords ?? []),
  ]
    .join(' ')
    .toLowerCase();
  return needles.every((n) => hay.includes(n));
}
